import { ICommunity } from 'app/models/community'
import { ICommunityBanner } from 'app/models/community-banner'
import { ICommunityUser } from 'app/models/community-user'
import { ICompany } from 'app/models/company'
import { ICompanyUser } from 'app/models/company-user'
import { ILocation } from 'app/models/location'
import { ILocationService } from 'app/models/location-service'
import { IRole } from 'app/models/role'
import { IUserType } from 'app/models/user-type'
import { createContext, createElement, Dispatch, SetStateAction, useContext, useState } from 'react'

interface IAppContext {
  setCurrentCompanies: Dispatch<SetStateAction<ICompany[]>>
  currentCompanies: ICompany[]
  setCurrentCommunity: Dispatch<SetStateAction<ICommunity | undefined>>
  currentCommunity: ICommunity | undefined
  setCurrentCommunityBanners: Dispatch<SetStateAction<ICommunityBanner[]>>
  currentCommunityBanners: ICommunityBanner[]
  setCurrentCompany: Dispatch<SetStateAction<ICompany | undefined>>
  currentCompany: ICompany | undefined
  setRoles: Dispatch<SetStateAction<IRole[]>>
  roles: IRole[]
  setCurrentCompanyUsers: Dispatch<SetStateAction<ICompanyUser[]>>
  currentCompanyUsers: ICompanyUser[]
  setCurrentCommunityUsers: Dispatch<SetStateAction<ICommunityUser[]>>
  currentCommunityUsers: ICommunityUser[]
  isCompanyAdmin: boolean | undefined
  setIsCompanyAdmin: Dispatch<SetStateAction<boolean | undefined>>
  isCommunityAdmin: boolean | undefined
  setIsContactperson: Dispatch<SetStateAction<boolean | undefined>>
  isContactperson: boolean | undefined
  setIsCommunityAdmin: Dispatch<SetStateAction<boolean | undefined>>
  currentLocations: ILocation[]
  setCurrentLocations: Dispatch<SetStateAction<ILocation[]>>
  currentCompanyLocations: ILocation[]
  setCurrentCompanyLocations: Dispatch<SetStateAction<ILocation[]>>
  currentLocationServices: { [locationId: string]: ILocationService[] }
  setCurrentLocationServices: Dispatch<SetStateAction<{ [locationId: string]: ILocationService[] }>>
  currentCompanyLocationServices: { [locationId: string]: ILocationService[] }
  setCurrentCompanyLocationServices: Dispatch<SetStateAction<{ [locationId: string]: ILocationService[] }>>
  setUserTypes: Dispatch<SetStateAction<IUserType[]>>
  userTypes: IUserType[]
}

const AppContext = createContext<IAppContext>({} as IAppContext)

export const useAppContext = () => {
  return useContext(AppContext)
}

export const AppContextProvider: React.FC = ({ children }) => {
  const [userTypes, setUserTypes] = useState<IUserType[]>([])
  const [currentCompanies, setCurrentCompanies] = useState<ICompany[]>([])
  const [currentCommunity, setCurrentCommunity] = useState<ICommunity | undefined>()
  const [currentCommunityBanners, setCurrentCommunityBanners] = useState<ICommunityBanner[]>([])
  const [currentCompany, setCurrentCompany] = useState<ICompany | undefined>()
  const [roles, setRoles] = useState<IRole[]>([])
  const [currentCompanyUsers, setCurrentCompanyUsers] = useState<ICompanyUser[]>([])
  const [currentCommunityUsers, setCurrentCommunityUsers] = useState<ICommunityUser[]>([])
  const [isCompanyAdmin, setIsCompanyAdmin] = useState<boolean | undefined>()
  const [isCommunityAdmin, setIsCommunityAdmin] = useState<boolean | undefined>()
  const [isContactperson, setIsContactperson] = useState<boolean | undefined>()
  const [currentLocations, setCurrentLocations] = useState<ILocation[]>([])
  const [currentCompanyLocations, setCurrentCompanyLocations] = useState<ILocation[]>([])
  const [currentLocationServices, setCurrentLocationServices] = useState<{ [locationId: string]: ILocationService[] }>(
    {}
  )
  const [currentCompanyLocationServices, setCurrentCompanyLocationServices] = useState<{
    [locationId: string]: ILocationService[]
  }>({})

  return createElement(
    AppContext.Provider,
    {
      value: {
        currentCompanies,
        setCurrentCompanies,
        currentCommunity,
        setCurrentCommunity,
        currentCommunityBanners,
        setCurrentCommunityBanners,
        roles,
        setRoles,
        currentCompanyUsers,
        setCurrentCompanyUsers,
        currentCompany,
        setCurrentCompany,
        isCompanyAdmin,
        setIsCompanyAdmin,
        isCommunityAdmin,
        setIsCommunityAdmin,
        currentCommunityUsers,
        setCurrentCommunityUsers,
        currentLocations,
        setCurrentLocations,
        currentCompanyLocations,
        setCurrentCompanyLocations,
        currentLocationServices,
        setCurrentLocationServices,
        currentCompanyLocationServices,
        setCurrentCompanyLocationServices,
        setIsContactperson,
        isContactperson,
        userTypes,
        setUserTypes
      },
    },
    children
  )
}
